import {Dialog} from '@priority-software/priority-style-react';
import React, {forwardRef, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {logIdToStageStepIndex, stageStepsMapping, successStepLogId} from './constants';
import {StepFactory} from './Steps';
import {FiscalYearState, FooterContext, SideBarContext} from './Components';
import styles from './EOYWizard.module.scss';
import {NextState, WizardStepEnum} from './types';
import {isRTL} from '../../Localization';
import classNames from 'classnames';
import {checkEoyRecord, getEoyLog, getLastUnfinishedStep} from './actions/wizardLog';
import {getNextMaxDialogsZIndex} from '../../_shared/GWTBridge/invoke/genPage';
import {closeFormBySession} from '../Form/actions/form';
import {
    eoyOwnerKey,
    eoyResponsibleKey,
    getEoyResponsible,
    setEoyResponsible,
    showEOYReminder
} from './actions/reminders';
import {Column, Form} from '../../stores/Form';
import {endProc} from '../../_shared/GWTBridge/invoke/procMain';
import {StepCompletedDialog} from './Components/StepCompletedDialog/StepCompletedDialog';
import texts from './texts';
import {fieldValue} from '../../stores/Form/Field';

export const EOYWizard = forwardRef((props: any, ref: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showCompletedStepMessage, setShowCompletedStepMessage] = useState(false);
    const [currentStageIndex, setCurrentStageIndex] = useState<number>(0);
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [customNextLabel, setCustomNextLabel] = useState<string | undefined>(undefined);
    const [onNextState, setOnNextState] = useState<NextState>(NextState.Idle);
    const [eoyOwner, setEoyOwner] = useState<string>('');
    const [stepResponsible, setStepResponsible] = useState<string>('');
    const [dialogZIndex, setDialogZIndex] = useState(getNextMaxDialogsZIndex());
    const [fiscalYearEndDate, setFiscalYearEndDate] = useState<string>();
    const [fiscalYearState, setFiscalYearState] = useState<FiscalYearState | undefined>(undefined);
    const [isFiscalYearStateLoaded, setIsFiscalYearStateLoaded] = useState<boolean>(false);

    const startStepIndex = useRef(0);

    const isRtl = isRTL();

    const updateZIndex = (zIndex: number) => {
        setDialogZIndex(zIndex);
    };

    const getZIndex = (): number => {
        return dialogZIndex;
    };

    const tryOpenWizardFromProc = async () => {
        const form: Form = await getEoyLog();
        showEOYReminder(false);

        if (form) {
            const isOk: boolean = checkEoyRecord(form);
            if (isOk) {
                const record = form.records[0];
                const stepId = await setCurrentStepFromLog(form);
                const wizardOwner = record?.getField(form.getColumnByName('USERLOGIN')?.id)
                    ?.origValue as string;

                const responsibleUsers = await getEoyResponsible();

                if (
                    stepId === stageStepsMapping[0].steps[1]?.logId &&
                    responsibleUsers[eoyOwnerKey]?.Str !== wizardOwner
                ) {
                    setEoyResponsible(wizardOwner, true);
                }

                setStepResponsible(responsibleUsers[eoyResponsibleKey]?.Str || wizardOwner);

                setIsOpen(true);
                setEoyOwner(wizardOwner);
            }
            closeFormBySession(form.session, false);
        }
    };

    const setCurrentStepFromLog = async (form: Form) => {
        const processEndDateColumn: Column = form.getColumnByName('ENDDATE');
        const processEndDate: fieldValue = form.currentRecord.getField(
            processEndDateColumn.id
        )?.origValue;
        const lastStageInd = stageStepsMapping.length - 1;
        const successWizardStep = stageStepsMapping[lastStageInd].steps.length - 1;

        if (processEndDate) {
            setCurrentStageIndex(lastStageInd);
            setCurrentStepIndex(successWizardStep);
            return null;
        }

        const lastUnfinishedStep = await getLastUnfinishedStep(form);
        setCurrentStep(lastUnfinishedStep);

        if (lastUnfinishedStep === successStepLogId) {
            setCurrentStepIndex(successWizardStep);
        }

        return lastUnfinishedStep;
    };

    const setCurrentStep = (lastUnfinishedStep: string) => {
        const stageStepInd = logIdToStageStepIndex(lastUnfinishedStep);
        setCurrentStageIndex(stageStepInd.stageIndex);
        startStepIndex.current = stageStepInd.stepIndex;
        setCurrentStepIndex(0);
    };

    const onStepComplete = () => {
        setShowCompletedStepMessage(true);
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                openWizard: tryOpenWizardFromProc,
                updateZIndex: updateZIndex,
                getZIndex: getZIndex,
                closeWizard: closeDialog,
                isWizardOpen: () => {
                    return isOpen;
                }
            };
        },
        [[dialogZIndex, getZIndex]]
    );

    const currentStep = useMemo(
        () => stageStepsMapping[currentStageIndex].steps[currentStepIndex],
        [stageStepsMapping, currentStageIndex, currentStepIndex]
    );

    const onCloseCallback = () => {
        setIsOpen(false);
        setFiscalYearState(undefined);
        setShowCompletedStepMessage(false);

        endProc();
    };

    const closeDialog = () => {
        if (
            stageStepsMapping[currentStageIndex]?.steps[currentStepIndex]?.logId ===
            successStepLogId
        ) {
            setOnNextState(NextState.Processing);
        } else {
            onCloseCallback();
        }
    };

    if (showCompletedStepMessage) {
        return (
            <StepCompletedDialog
                onClose={closeDialog}
                currentStepName={texts[currentStep.name]}
                owner={eoyOwner}
            />
        );
    }

    return (
        <Dialog
            componentClasses={classNames(
                styles.dialogWrapper,
                currentStep.type === WizardStepEnum.Welcome
                    ? styles.modalWelcome
                    : styles.modalCommon,
                {
                    [styles.rtlWrapper]: isRtl
                }
            )}
            isOpen={isOpen}
            style={{display: dialogZIndex > -1 ? 'block' : 'none'}}
            onClose={closeDialog}
            zIndex={dialogZIndex}
            mask={false}
            content={
                <SideBarContext.Provider
                    value={{
                        currentStepIndex,
                        currentStageIndex,
                        startStepIndex: startStepIndex.current,
                        setCurrentStepIndex,
                        setCurrentStageIndex,
                        stageStepsMapping,
                        currentStep,
                        owner: eoyOwner,
                        stepResponsible
                    }}
                >
                    <FooterContext.Provider
                        value={{
                            isValid,
                            setIsValid,
                            customNextLabel,
                            setCustomNextLabel,
                            onCloseCallback,
                            onNextState,
                            setOnNextState,
                            dialogZIndex,
                            onStepComplete,
                            fiscalYearEndDate,
                            setFiscalYearEndDate,
                            fiscalYearState: {
                                isLoaded: isFiscalYearStateLoaded,
                                setIsLoaded: setIsFiscalYearStateLoaded,
                                state: fiscalYearState,
                                setState: setFiscalYearState
                            }
                        }}
                    >
                        <StepFactory />
                    </FooterContext.Provider>
                </SideBarContext.Provider>
            }
        />
    );
});
